import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import apiClient from "@/lib/apiClient";
import { trackLogin } from "@/lib/analytics";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("kraus_token");
    if (!token) {
      setLoading(false);
      return;
    }
    apiClient
      .get("/auth/me")
      .then((res) => {
        setUser(res.data.user);
        setCompany(res.data.company);
      })
      .catch(() => localStorage.removeItem("kraus_token"))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await apiClient.post("/auth/login", { email, password });
    localStorage.setItem("kraus_token", res.data.token);
    setUser(res.data.user);
    setCompany(res.data.company);
    trackLogin("email");  // GA4 standard login, generic method only (no PII)
    return res.data;
  }, []);

  const register = useCallback(async (payload) => {
    const res = await apiClient.post("/auth/register", payload);
    localStorage.setItem("kraus_token", res.data.token);
    setUser(res.data.user);
    setCompany(res.data.company);
    return res.data;
  }, []);

  const refreshCompany = useCallback(async () => {
    const res = await apiClient.get("/company");
    setCompany(res.data);
    return res.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("kraus_token");
    setUser(null);
    setCompany(null);
    window.location.href = "/login";
  }, []);

  const value = useMemo(
    () => ({ user, company, loading, login, register, logout, refreshCompany }),
    [user, company, loading, login, register, logout, refreshCompany]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
